import type { VehicleHit } from '../api/catalog';
import { ca } from '../i18n/ca';

/**
 * The ways in and out as the quick start's "Sortida" step offers them. The
 * options are the recipes' own words ("a peu", "helicòpter"…), because those
 * are what the generator reads (`MissionGenerator.means`) and what a vote shows
 * the players; `meansOf` is the definition's name for each.
 */
export type Means = 'foot' | 'vehicle' | 'helicopter' | 'boat' | 'parachute' | 'deployed';

/** What reaches a shore from a ship; nothing else is offered there (QUICK_START_REVIEW.md F3). */
export const FROM_SEA = ['helicòpter', 'barca'];

/** Every way out, whatever the way in was. */
export const WAYS_OUT = ['a peu', 'vehicle', 'helicòpter', 'barca'];

/** The definition's name for an option, the way `MissionGenerator.means` reads it. */
export function meansOf(option: string): Means {
  const o = option.toLowerCase();
  if (o.startsWith('helic')) return 'helicopter';
  if (o.startsWith('vehicl')) return 'vehicle';
  if (o.startsWith('barca') || o.startsWith('boat')) return 'boat';
  if (o.startsWith('paraca') || o.startsWith('parachute')) return 'parachute';
  if (o.startsWith('ja desplegats') || o.startsWith('deployed')) return 'deployed';
  return 'foot';
}

/** The ways in on offer: from a ship a helicopter or a boat, from land the recipe's own. */
export function waysIn(
  recipeOptions: string[],
  from: 'land' | 'sea',
  era?: string | null,
): string[] {
  // From the sea in the Second World War is a beach and landing craft (the owner,
  // 2026-09-19): nothing hovers in 1944, so the one way off the sand is a boat.
  if (from === 'sea' && era === 'ww2') return ['barca'];
  if (from === 'sea') return FROM_SEA;
  // A recipe that walks in from a land base may drive in from it: the base always
  // has the party's ground vehicles (the owner, 2026-10-11), and a night raid
  // offered only «helicòpter» and «a peu» from a base whose vehicles were three
  // helicopters (the owner, 2026-10-19). Whether the recipe admits it is still
  // its own constraints' to say (`admitsIn`).
  const walks = recipeOptions.some((o) => meansOf(o) === 'foot');
  const drives = recipeOptions.some((o) => meansOf(o) === 'vehicle');
  return walks && !drives ? [...recipeOptions, 'vehicle'] : recipeOptions;
}

/**
 * The means of both lists that need something to travel in, each once, in order.
 *
 * <p>A helicopter is proposed for the base only for a way in (the owner's rule,
 * 2026-10-11: no helicopter stands at the base without a job). A way out by
 * helicopter is the transport support's, which comes to where the players are;
 * helicopters parked at the base cannot fetch anybody, and three of them stood
 * idle at the base of a raid on foot (mission 97745zk).
 */
export function meansNeedingVehicles(inMeans: string[], outMeans: string[]): Means[] {
  const out: Means[] = [];
  const add = (m: Means) => {
    if (!out.includes(m)) out.push(m);
  };
  for (const option of inMeans) {
    const m = meansOf(option);
    if (m === 'helicopter' || m === 'boat' || m === 'vehicle') add(m);
  }
  for (const option of outMeans) {
    const m = meansOf(option);
    if (m === 'boat' || m === 'vehicle') add(m);
  }
  return out;
}

/** The catalogue's kinds each means travels in. */
const KINDS: Partial<Record<Means, string[]>> = {
  vehicle: ['car', 'land', 'apc', 'tank'],
  helicopter: ['heli'],
  boat: ['ship'],
};

/**
 * At most this many of one means at the base: the generator's six ground
 * vehicles (`MissionGenerator.MAX_GROUND_VEHICLES`) and the runtime's four
 * helicopters (`fn_playerVehiclesInit`); the list itself holds twelve.
 */
export const MOST_AT_BASE: Partial<Record<Means, number>> = { vehicle: 6, helicopter: 4 };
const MOST_IN_LIST = 12;

/** The means whose seats must take every player: what the party rides from the base. */
export const SEATED: Means[] = ['vehicle', 'helicopter'];

/** Whether a vehicle of the catalogue travels by this means. */
export function travelsBy(hit: VehicleHit | undefined, means: Means): hit is VehicleHit {
  return !!hit && (KINDS[means] ?? []).includes(hit.kind);
}

/**
 * The seats a list holds for one means, by the catalogue's own count, and how
 * many of its vehicles go by it. A class nobody has described yet counts for
 * nothing until it is.
 */
export function seatsFor(
  list: string[],
  hits: Record<string, VehicleHit>,
  means: Means,
): { seats: number; count: number } {
  let seats = 0;
  let count = 0;
  for (const c of list) {
    const hit = hits[c];
    if (!travelsBy(hit, means)) continue;
    count++;
    seats += Math.max(0, hit.transportSoldier);
  }
  return { seats, count };
}

/**
 * The list with as many more vehicles of one means as it takes to seat every
 * player: more of the roomiest one it already has, else of `fallback` (the
 * faction's own transport for that means), never past the base's limit. The
 * same list when the seats are already enough or nothing can be added; what is
 * still missing is then the step's to say.
 */
export function fitEveryone(
  list: string[],
  hits: Record<string, VehicleHit>,
  means: Means,
  players: number,
  fallback: VehicleHit | null = null,
): string[] {
  const { seats, count } = seatsFor(list, hits, means);
  if (seats >= players) return list;
  let pick: VehicleHit | null = null;
  for (const c of list) {
    const hit = hits[c];
    if (travelsBy(hit, means) && hit.transportSoldier > 0) {
      if (!pick || hit.transportSoldier > pick.transportSoldier) pick = hit;
    }
  }
  if (!pick && fallback && travelsBy(fallback, means) && fallback.transportSoldier > 0) {
    pick = fallback;
  }
  if (!pick) return list;
  const each = pick.transportSoldier;
  const room = Math.min((MOST_AT_BASE[means] ?? MOST_IN_LIST) - count, MOST_IN_LIST - list.length);
  const more = Math.min(room, Math.ceil((players - seats) / each));
  if (more <= 0) return list;
  const className = pick.className;
  return [...list, ...Array.from({ length: more }, () => className)];
}

/** How a sentence says a list of ways: "en helicòpter", or "en helicòpter o a peu (es vota)". */
export function phraseOf(options: string[], by: string): string {
  const t = ca.missionUi.start;
  if (options.length === 0) return t.phrases.foot;
  const text = options.map((o) => t.phrases[meansOf(o)] ?? o).join(' o ');
  if (options.length === 1) return text;
  return by === 'voteBefore' ? t.voted(text) : t.creatorLater(text);
}
